import React, { useState, useRef, useEffect } from 'react';
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, BarElement, CategoryScale, ScatterController, LineElement, PointElement, LinearScale, Title, Tooltip, Legend } from 'chart.js';
import { Scatter } from 'react-chartjs-2';
import CanvasJSReact from '@canvasjs/react-charts';

ChartJS.register(BarElement, CategoryScale, ScatterController, LineElement, PointElement, LinearScale, Title, Tooltip, Legend);

const PyramidChart = ({ predictionsQuarterlyData }) => {
    const combinedArray = predictionsQuarterlyData.features.map((feature, index) => ({
        feature,
        contribution: predictionsQuarterlyData.contributions[index],
        explanation: predictionsQuarterlyData.explanations[index],
        value: predictionsQuarterlyData.values[index]
    }));
    
    // Sort the combined array by contributions in descending order
    const sortedArray = combinedArray.sort((a, b) => b.contribution - a.contribution);

    const CanvasJS = CanvasJSReact.CanvasJS;
    const CanvasJSChart = CanvasJSReact.CanvasJSChart;
    const pyramidColors = ['rgba(18, 65, 114, 1)', 'rgba(18, 65, 114, 0.9)', 'rgba(18, 65, 114, 0.7)', 'rgba(18, 65, 114, 0.5)', 'rgba(18, 65, 114, 0.4)', 'rgba(18, 65, 114, 0.2)'];
    const [isModalOpen, setIsModalOpen] = useState(false);
    const recommendationModalRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [elitePerformanceProtocols, setElitePerformanceProtocols] = useState([]);
    // const [selectedPeriod, setSelectedPeriod] = useState('monthly-score');
    const [features, setFeatures] = useState(predictionsQuarterlyData.features);
    const [values, setValues] = useState(predictionsQuarterlyData.values);
    const [recommendations, setRecommendations] = useState(predictionsQuarterlyData.recommendations);
    const [suggestions, setSuggestions] = useState(predictionsQuarterlyData.suggestions);
    const [sortedFeatures, setSortedFeatures] = useState(sortedArray.map(item => item.feature));
    const [sortedContributions, setSortedContributions] = useState(sortedArray.map(item => item.contribution));
    const [sortedExplanations, setSortedExplanations] = useState(sortedArray.map(item => item.explanation));
    const [sortedValues, setSortedValues] = useState(sortedArray.map(item => item.value));
    const [showBarChart, setShowBarChart] = useState(false);
    const [selectedBarChartFeature, setSelectedBarChartFeature] = useState();
    // const [loading, setLoading] = useState(false);

    const openModal = () => {
        setIsModalOpen(true);
        document.body.style.overflow = 'hidden'; // Prevent body scroll
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setActiveTab(0);
        setShowBarChart(false);
        document.body.style.overflow = 'auto'; // Restore body scroll
    };

    const handleClickOutside = (event) => {
        if (recommendationModalRef.current && !recommendationModalRef.current.contains(event.target)) {
            closeModal();
        }
    };

    // useEffect(() => {
    //     const updateData = (predictionsData) => {
    //         const { features, contributions, values, recommendations, explanations, suggestions } = predictionsData;
    //         const combinedArray = features.map((feature, index) => ({
    //             feature,
    //             contribution: contributions[index],
    //             explanation: explanations[index],
    //             value: values[index]
    //         }));
            
    //         // Sort the combined array by contributions in descending order
    //         const sortedArray = combinedArray.sort((a, b) => b.contribution - a.contribution);
            
    //         // Extract the sorted arrays
    //         const sortedFeatures = sortedArray.map(item => item.feature);
    //         const sortedContributions = sortedArray.map(item => item.contribution);
    //         const sortedExplanations = sortedArray.map(item => item.explanation);
    //         const sortedValues = sortedArray.map(item => item.value);
    
    //         setFeatures(features);
    //         setValues(values);
    //         setRecommendations(recommendations);
    //         setSuggestions(suggestions);
    //         setSortedFeatures(sortedFeatures);
    //         setSortedContributions(sortedContributions);
    //         setSortedExplanations(sortedExplanations);
    //         setSortedValues(sortedValues);
    //         setLoading(false);
    //     };

    //     if(selectedPeriod === 'monthly-score') {
    //         updateData(predictionsMonthlyData);
    //     } else if(selectedPeriod === 'quarterly-score') {
    //         updateData(predictionsQuarterlyData);
    //     }
    // }, [selectedPeriod]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const newProtocol = protocols.filter((protocol) => {
            if (features.indexOf(protocol.column) !== -1) {
                return protocol;
            }
        });
        setElitePerformanceProtocols(newProtocol);
    }, []);

    const protocols = [
        {
            "column": "Average Heart Rate",
            "heading": "Heart Rate Deceleration",
            "challenge": "Elevated pre-sleep heart rate ({val} bpm) hinders deep sleep and HRV stability.",
            "goal": "Maintain optimal heart rate to improve your autonomic recovery.",
            "how":
                [
                    {
                        "heading": "Incorporate Cooling Techniques Before Bed",
                        "text": "Take a warm shower or bath 60–90 minutes before bed to initiate core body temperature reduction.",
                    },
                    {
                        "heading": "Enhance Your Wind-Down Routine",
                        "text": "Perform light stretching with a focus on long exhales (e.g., inhale for 4 seconds, exhale for 6–8 seconds).",
                    },
                    {
                        "heading": "Practice Relaxation Techniques",
                        "text": "Practice resonance breathing (4s inhale, 6s exhale) for 10–15 minutes in dim lighting before bed.",
                    },
                    {
                        "heading": "Avoid Stimulants and Heavy Meals",
                        "text": "Avoid alcohol, heavy meals, or caffeine at least 2 hours before bedtime.",
                    },
                    {
                        "heading": "Optimize Your Sleep Environment",
                        "text": "Dim all lights and set your room temperature to 67–70°F to promote relaxation.",
                    }
                ]
        },
        {
            "column": "Total Sleep Duration",
            "heading": "Increase Sleep Duration",
            "challenge": "Total sleep duration is {val}, below the recommended 8:00–9:00 hours needed for optimal recovery and HRV stability.",
            "goal": "Increase total sleep duration by 30–45 minutes to enhance recovery and improve nocturnal HRV.",
            "how":
                [
                    {
                        "heading": "Set a Consistent Sleep Schedule",
                        "text": "Maintain a fixed bedtime and wake-up time to reinforce your circadian rhythm. Aim for lights out by 10:30 PM to maximize sleep opportunity."
                    },
                    {
                        "heading": "Extend Wind-Down Routine",
                        "text": "Begin your pre-sleep routine 15 minutes earlier than usual, incorporating calming activities like reading, journaling, or light meditation."
                    },
                    {
                        "heading": "Avoid Evening Stimulants",
                        "text": "Avoid alcohol, caffeine, and heavy meals at least 2 hours before bed to reduce sleep disruptions."
                    },
                    {
                        "heading": "Optimize Sleep Environment",
                        "text": "Ensure your room is cool (67–70°F), dark, and free of noise. Use blackout curtains or an eye mask if necessary."
                    },
                    {
                        "heading": "Practice Relaxation Techniques",
                        "text": "Perform 15 to 20 minutes of resonance breathing (4-second inhale, 6-second exhale) in dim lighting to promote the body’s ability to let go and restore. It also helps to turn off the busy brain to allow for sleep."
                    },
                    {
                        "heading": "Monitor Screen Usage",
                        "text": "Avoid blue light exposure from screens 60 minutes before bed to support natural melatonin production."
                    }
                ]
        },
        {
            "column": "REM Sleep Duration",
            "heading": "Increase Sleep Duration",
            "challenge": "Elevated REM sleep duration of {val}, hinders optimal recovery and HRV stability.",
            "goal": "Decrease REM sleep duration by 30–45 minutes to enhance recovery and improve nocturnal HRV.",
            "how":
                [
                    {
                        "heading": "Set a Consistent Sleep Schedule",
                        "text": "Maintain a fixed bedtime and wake-up time to reinforce your circadian rhythm. Aim for lights out by 10:30 PM to maximize sleep opportunity."
                    },
                    {
                        "heading": "Extend Wind-Down Routine",
                        "text": "Begin your pre-sleep routine 15 minutes earlier than usual, incorporating calming activities like reading, journaling, or light meditation."
                    },
                    {
                        "heading": "Avoid Evening Stimulants",
                        "text": "Avoid alcohol, caffeine, and heavy meals at least 2 hours before bed to reduce sleep disruptions."
                    },
                    {
                        "heading": "Optimize Sleep Environment",
                        "text": "Ensure your room is cool (67–70°F), dark, and free of noise. Use blackout curtains or an eye mask if necessary."
                    },
                    {
                        "heading": "Practice Relaxation Techniques",
                        "text": "Perform 15 to 20 minutes of resonance breathing (4-second inhale, 6-second exhale) in dim lighting to promote the body’s ability to let go and restore. It also helps to turn off the busy brain to allow for sleep."
                    },
                    {
                        "heading": "Monitor Screen Usage",
                        "text": "Avoid blue light exposure from screens 60 minutes before bed to support natural melatonin production."
                    }
                ]
        },
        {
            "column": "Bedtime Start",
            "heading": "Maintain Consistent Bedtime Start",
            "challenge": "Variability in bedtime disrupts circadian rhythm alignment, which can negatively impact sleep quality, recovery, and nocturnal HRV.",
            "goal": "Consistently start your bedtime routine by 10:20 PM to stabilize your circadian rhythm and support improvements in nocturnal HRV.",
            "how":
                [
                    {
                        "heading": "Set a Pre-Bed Alarm",
                        "text": "Use an evening alarm or reminder to initiate your wind-down routine at 9:50 PM. This helps ensure you’re transitioning toward sleep at the appropriate time."
                    },
                    {
                        "heading": "Establish a Wind-Down Routine",
                        "text": "Dedicate the 30 minutes before bed to activities that reduce cognitive and physical arousal. Examples include reading, journaling, or engaging in light stretching. Avoid stimulating activities like intense conversations or screen time."
                    },
                    {
                        "heading": "Eliminate Sleep Disruptors",
                        "text": "Avoid caffeine and alcohol within 6 hours of bedtime, as they interfere with sleep onset and depth. Keep meals light if eating within 2 hours of bed."
                    },
                    {
                        "heading": "Control Light Exposure",
                        "text": "Minimize exposure to bright or artificial light during the hour before bed. Use dim lighting and consider wearing blue light-blocking glasses to enhance melatonin production."
                    },
                    {
                        "heading": "Track and Reflect",
                        "text": "Use a sleep tracker or log to monitor your bedtime consistency. Aim to keep your bedtime start within a 15-minute range of 10:20 PM each night for optimal results."
                    }
                ]
        },
        {
            "column": "Total Calories",
            "heading": "Maintain Optimal Caloric Intake",
            "challenge": "Inconsistent caloric intake can disrupt energy balance and recovery, potentially impacting nocturnal HRV and overall autonomic function.",
            "goal": "Maintain your total daily caloric intake of {val} calories to support optimal recovery and improve nocturnal HRV.",
            "how":
                [
                    {
                        "heading": "Track Caloric Intake",
                        "text": "Use a food tracker or app to monitor your daily caloric intake, ensuring it aligns with the target of {val} calories."
                    },
                    {
                        "heading": "Prioritize Nutrient-Dense Foods",
                        "text": "Focus on whole, nutrient-dense foods such as lean proteins, healthy fats, and complex carbohydrates to provide sustained energy and support recovery processes."
                    },
                    {
                        "heading": "Distribute Calories Consistently",
                        "text": "Spread caloric intake evenly across meals to stabilize blood sugar and reduce stress on your autonomic nervous system. Aim for balanced meals at regular intervals (e.g., breakfast, lunch, dinner, and a light snack)."
                    },
                    {
                        "heading": "Avoid Late-Night Overeating",
                        "text": "Limit heavy meals or excessive snacking within 2 hours of bedtime to prevent disruptions to digestion and HRV."
                    },
                    {
                        "heading": "Evaluate Activity Levels",
                        "text": "Adjust caloric intake slightly based on activity levels, ensuring you meet your energy needs without over- or under-consuming."
                    }
                ]
        },
        {
            "column": "Sleep Latency",
            "heading": "Optimize Sleep Latency",
            "challenge": "Sleep latency, or the time it takes to fall asleep, can influence overall sleep quality and nocturnal HRV. While maintaining a sleep latency of {val} minutes has not yet shown measurable improvement in HRV, further optimization may enhance recovery and autonomic balance.",
            "goal": "Reduce sleep latency to under 30 minutes to improve overall sleep efficiency and support nocturnal HRV stability.",
            "how":
                [
                    {
                        "heading": "Enhance Your Wind-Down Routine",
                        "text": "Begin calming activities such as reading, journaling, or light stretching at least 30 minutes before bed to signal to your body that it’s time to sleep."
                    },
                    {
                        "heading": "Practice Relaxation Techniques",
                        "text": "Incorporate 10–15 minutes of resonance breathing (e.g., 4-second inhale, 6-second exhale) in dim lighting to promote relaxation and reduce pre-sleep arousal."
                    },
                    {
                        "heading": "Avoid Stimulants and Heavy Meals",
                        "text": "Limit caffeine, alcohol, and large meals within 4 hours of bedtime to prevent disruptions to your ability to fall asleep."
                    },
                    {
                        "heading": "Regulate Light Exposure",
                        "text": "Minimize blue light exposure from screens 1 hour before bed to enhance natural melatonin production. Consider wearing blue light-blocking glasses if screen use is unavoidable."
                    },
                    {
                        "heading": "Optimize Your Sleep Environment",
                        "text": "Maintain a cool, dark room at 67–70°F and use blackout curtains or a sleep mask to eliminate potential disruptions."
                    },
                    {
                        "heading": "Consider Trying Lyma Supplement",
                        "text": "Take Lyma supplements as recommended in the evening to help wind down, monitoring sleep latency and nocturnal HRV for 7–10 days to evaluate effectiveness. These supplements contain ingredients like ashwagandha and magnesium, which support relaxation, reduce inflammation, and improve sleep quality."
                    }
                ]
        },
        {
            "column": "Average Breathing Rate",
            "heading": "Optimize Average Breathing Rate",
            "challenge": "Breathing rate directly impacts autonomic function and recovery, influencing your nocturnal HRV. Maintaining an average breathing rate of {val} breaths per minute has contributed to a 2 ms improvement in your nocturnal HRV, but further refinement may yield additional benefits.",
            "goal": "Sustain or reduce your breathing rate to 12–14 breaths per minute during sleep and pre-sleep to further optimize parasympathetic activation and HRV stability.",
            "how":
                [
                    {
                        "heading": "Incorporate Pre-Sleep Resonance Breathing",
                        "text": "Practice resonance breathing (4s inhale, 6s exhale) for 10–15 minutes in dim lighting before bed."
                    },
                    {
                        "heading": "Monitor Breathing Patterns During the Day",
                        "text": "Incorporate slow, diaphragmatic breathing throughout the day to train your body for a lower baseline breathing rate, as daytime breathing habits can affect nighttime rates. Avoiding shallow or rapid breathing during the day can help lower average breathing rates during sleep."
                    },
                    {
                        "heading": "Optimize Sleep Position",
                        "text": "Use a supportive pillow to maintain proper spinal alignment and reduce strain on your airways, as sleep position can significantly affect breathing efficiency. For instance, lying on your back with your head slightly elevated can enhance respiratory patterns."
                    },
                    {
                        "heading": "Evaluate Environmental Factors",
                        "text": "Maintain a clean sleeping environment by using an air purifier and avoiding potential allergens to help prevent disruptions in respiratory patterns. Poor air quality and allergens can increase respiratory effort, leading to higher breathing rates during sleep."
                    }
                ]
        },
        {
            "column": "REM to Deep Sleep Ratio",
            "heading": "Optimize REM to Deep Sleep Ratio",
            "challenge": "An imbalanced REM to deep sleep ratio can impact recovery quality and nocturnal HRV. While REM sleep is essential for cognitive recovery, insufficient deep sleep may limit physical recovery and autonomic reset, reducing HRV stability.",
            "goal": "Shift the balance toward a healthier ratio by increasing deep sleep duration to optimize recovery and improve nocturnal HRV.",
            "how":
                [
                    {
                        "heading": "Enhance Pre-Sleep Relaxation",
                        "text": "Incorporate 10–15 minutes of resonance breathing (4-second inhale, 6-second exhale) or light stretching in dim lighting before bed to lower pre-sleep arousal. Deep sleep is most effectively initiated when the body is in a relaxed state. Stress or high arousal levels before bed can reduce deep sleep duration."
                    },
                    {
                        "heading": "Adjust Sleep Environment",
                        "text": "Maintain your room at 67–69°F, use blackout curtains or an eye mask, and eliminate potential noise with a white noise machine if needed. A cool, dark, and quiet environment supports the onset and maintenance of deep sleep."
                    },
                    {
                        "heading": "Monitor Light and Screen Exposure",
                        "text": "Avoid screens at least 60 minutes before bed or wear blue light-blocking glasses if screen use is unavoidable. Exposure to blue light in the evening suppresses melatonin production, which is critical for transitioning into deep sleep."
                    },
                    {
                        "heading": "Limit Alcohol and Late-Night Meals",
                        "text": "Avoid alcohol within 4 hours of bed and limit food intake to light snacks at least 2 hours before sleep. Alcohol and heavy meals close to bedtime can disrupt sleep architecture, reducing deep sleep duration."
                    },
                    {
                        "heading": "Consider Huberman's Sleep Cocktail Before Bed",
                        "text": "Take these supplements (Huberman’s sleep cocktail) 30–60 minutes before bed to improve relaxation and sleep quality. Magnesium (200-400 mg) for muscle relaxation and nervous tension reduction, Apigenin (50 mg) to calm the nervous system, L-Theanine (100-200 mg) for sedation-free relaxation and deeper sleep."
                    }
                ]
        },
        {
            "column": "Steps",
            "heading": "Optimize Daily Step Count for Optimal Movement and Sleep",
            "challenge": "Achieving optimal levels of daily movement is essential for regulating your circadian rhythm, improving recovery, and enhancing nocturnal HRV. Maintaining a step count of {val} reflects ideal activity levels to support these processes.",
            "goal": "Sustain a daily step count of 15,000–16,000 steps to optimize physical activity, autonomic balance, and sleep quality.",
            "how":
                [
                    {
                        "heading": "Integrate Movement Throughout the Day",
                        "text": "Begin your day with a 30-minute brisk walk or jog, add short post-meal walks, and take 5–10-minute walking breaks hourly to decrease sedentary time and maintain consistent energy levels throughout the day."
                    },
                    {
                        "heading": "Leverage Non-Exercise Activity Thermogenesis (NEAT)",
                        "text": "NEAT includes all physical activities outside structured workouts, significantly increasing total energy expenditure and step counts. Boosting NEAT by choosing active commutes like walking or cycling for short trips, walking during phone calls, rearranging spaces to encourage standing, and taking longer routes while running errands can naturally optimize movement levels."
                    },
                    {
                        "heading": "Pair Steps with Sunlight Exposure",
                        "text": "Spend 15–20 minutes walking outside, preferably within the first two hours of waking, to synchronize your circadian rhythm and boost your step count. Morning walks offer the dual benefits of increasing steps and regulating your internal clock through sunlight exposure, which promotes better sleep at night."
                    },
                    {
                        "heading": "Track Steps to Stay Consistent",
                        "text": "Use a fitness tracker to record your steps daily and identify opportunities to add movement during low-activity periods. Monitoring your progress ensures you consistently hit your target and make necessary adjustments."
                    },
                    {
                        "heading": "Time Activity to Support Sleep",
                        "text": "Physical activity earlier in the day enhances sleep drive, while overactivity close to bedtime can delay sleep onset and impact recovery. Aim to accumulate most of your daily steps before dinner and keep evening walks low-intensity and relaxing to prevent overstimulation before bedtime."
                    }
                ]
        },
        {
            "column": "Equivalent Walking Distance",
            "heading": "Optimize daily distance walked for Optimal Movement and Sleep",
            "challenge": "Achieving optimal levels of daily movement is essential for regulating your circadian rhythm, improving recovery, and enhancing nocturnal HRV. Maintaining a step count of {val} reflects ideal activity levels to support these processes.",
            "goal": "Sustain an optimal walking distance to optimize physical activity, autonomic balance, and sleep quality.",
            "how":
                [
                    {
                        "heading": "Integrate Movement Throughout the Day",
                        "text": "Begin your day with a 30-minute brisk walk or jog, add short post-meal walks, and take 5–10-minute walking breaks hourly to decrease sedentary time and maintain consistent energy levels throughout the day."
                    },
                    {
                        "heading": "Leverage Non-Exercise Activity Thermogenesis (NEAT)",
                        "text": "NEAT includes all physical activities outside structured workouts, significantly increasing total energy expenditure and step counts. Boosting NEAT by choosing active commutes like walking or cycling for short trips, walking during phone calls, rearranging spaces to encourage standing, and taking longer routes while running errands can naturally optimize movement levels."
                    },
                    {
                        "heading": "Pair Steps with Sunlight Exposure",
                        "text": "Spend 15–20 minutes walking outside, preferably within the first two hours of waking, to synchronize your circadian rhythm and boost your step count. Morning walks offer the dual benefits of increasing steps and regulating your internal clock through sunlight exposure, which promotes better sleep at night."
                    },
                    {
                        "heading": "Track Steps to Stay Consistent",
                        "text": "Use a fitness tracker to record your steps daily and identify opportunities to add movement during low-activity periods. Monitoring your progress ensures you consistently hit your target and make necessary adjustments."
                    },
                    {
                        "heading": "Time Activity to Support Sleep",
                        "text": "Physical activity earlier in the day enhances sleep drive, while overactivity close to bedtime can delay sleep onset and impact recovery. Aim to accumulate most of your daily steps before dinner and keep evening walks low-intensity and relaxing to prevent overstimulation before bedtime."
                    }
                ]
        },
        {
            "column": "Total Activity Time",
            "heading": "Optimize Activity Time",
            "challenge": "Sub-optimal activity time can reduce energy expenditure and limit autonomic flexibility, negatively impacting HRV.",
            "goal": "Reduce sedentary periods by incorporating more dynamic low-intensity activities to maintain recovery and enhance autonomic balance.",
            "how":
                [
                    {
                        "heading": "Stand or Walk During Breaks",
                        "text": "Set reminders to stand and stretch every hour or take a quick walk during work breaks. Breaking up long sedentary periods helps improve circulation and autonomic engagement."
                    },
                    {
                        "heading": "Incorporate Light Mobility Work",
                        "text": "Dedicate 10–15 minutes to stretches or yoga poses in the morning or evening. Gentle stretching or mobility exercises maintain low activity while promoting relaxation."
                    },
                    {
                        "heading": "Use Walking Meetings",
                        "text": "Take calls on a headset or use a treadmill desk to combine work and movement. Walking during phone calls or meetings increases low-intensity movement throughout the day."
                    },
                    {
                        "heading": "Engage in Household Activities",
                        "text": "Incorporate intentional movement into chores to keep your body engaged. Everyday tasks like cleaning or gardening contribute to low activity without requiring additional time."
                    }
                ]
        },
        {
            "column": "Deep Sleep Duration",
            "heading": "Optimize Deep Sleep Duration",
            "challenge": "Insufficient deep sleep reduces physical recovery and impairs autonomic reset, limiting HRV improvements.",
            "goal": "Increase deep sleep duration to 90 minutes or more for optimal physical recovery and HRV stability.",
            "how":
                [
                    {
                        "heading": "Create a Calming Bedtime Routine",
                        "text": "Spend 30 minutes before bed engaging in activities like reading, journaling, or light stretching. Preparing your mind and body for sleep enhances transitions into deep sleep stages."
                    },
                    {
                        "heading": "Optimize Sleep Environment",
                        "text": "Maintain room temperature at 67–69°F, use blackout curtains, and eliminate noise with a white noise machine. A cool, dark, and quiet bedroom promotes deeper, uninterrupted sleep."
                    },
                    {
                        "heading": "Time Physical Activity Earlier",
                        "text": "Finish high-intensity workouts at least 3 hours before bed to allow your body to downregulate. Exercise too close to bedtime can interfere with deep sleep onset."
                    },
                    {
                        "heading": "Incorporate Magnesium or Huberman's Sleep Cocktail",
                        "text": "Take magnesium glycinate or Huberman’s sleep cocktail (e.g., magnesium, apigenin, L-theanine) 30–60 minutes before bed. Magnesium and other calming supplements support deep sleep by relaxing the nervous system."
                    }
                ]
        },
        {
            "column": "Time in Bed",
            "heading": "Optimize Time in Bed",
            "challenge": "Spending either too much or too little time in bed can disrupt sleep efficiency and recovery.",
            "goal": "Align total time in bed with your sleep needs, ensuring enough time for full recovery without exceeding wakeful periods.",
            "how":
                [
                    {
                        "heading": "Set Consistent Sleep and Wake Times",
                        "text": "Go to bed and wake up at the same time every day, even on weekends. A regular schedule aligns circadian rhythms and optimizes time in bed for recovery."
                    },
                    {
                        "heading": "Monitor Sleep Efficiency",
                        "text": "Track efficiency with a sleep tracker and aim for 85% or higher. High time in bed with low sleep efficiency suggests fragmented or disrupted sleep."
                    },
                    {
                        "heading": "Avoid Excessive Wakefulness in Bed",
                        "text": "Only go to bed when sleepy, and get out of bed if you’re awake for more than 20 minutes. Spending long periods awake in bed disrupts the association between bed and sleep."
                    },
                    {
                        "heading": "Adjust Time in Bed Based on Sleep Duration",
                        "text": "If you consistently wake up before your alarm, adjust your bedtime slightly later to reduce unnecessary time in bed. Aligning time in bed with sleep needs ensures a balance between sufficient recovery and maintaining sleep efficiency."
                    }
                ]
        },
        {
            "column": "REM and Deep Sleep Duration",
            "heading": "Optimize REM and Deep Sleep Duration",
            "challenge": "Insufficient Deep sleep and REM sleep reduces physical recovery and impairs autonomic reset, limiting HRV improvements.",
            "goal": "Increase REM and Deep sleep duration to 90 minutes or more for optimal physical recovery and HRV stability.",
            "how":
                [
                    {
                        "heading": "Create a Calming Bedtime Routine",
                        "text": "Spend 30 minutes before bed engaging in activities like reading, journaling, or light stretching. Preparing your mind and body for sleep enhances transitions into deep sleep stages."
                    },
                    {
                        "heading": "Optimize Sleep Environment",
                        "text": "Maintain room temperature at 67–69°F, use blackout curtains, and eliminate noise with a white noise machine. A cool, dark, and quiet bedroom promotes deeper, uninterrupted sleep."
                    },
                    {
                        "heading": "Time Physical Activity Earlier",
                        "text": "Finish high-intensity workouts at least 3 hours before bed to allow your body to downregulate. Exercise too close to bedtime can interfere with deep sleep onset."
                    },
                    {
                        "heading": "Monitor Screen Usage",
                        "text": "Avoid blue light exposure from screens 60 minutes before bed to support natural melatonin production."
                    },
                    {
                        "heading": "Incorporate Magnesium or Huberman's Sleep Cocktail",
                        "text": "Take magnesium glycinate or Huberman’s sleep cocktail (e.g., magnesium, apigenin, L-theanine) 30–60 minutes before bed. Magnesium and other calming supplements support deep sleep by relaxing the nervous system."
                    }
                ]
        },
        {
            "column": "Temperature Deviation",
            "heading": "Stabilize Nighttime Temperature for Recovery",
            "challenge": "Increased nighttime temperature deviation of {val}°F suggests poor thermoregulation, which can disrupt sleep stages and reduce HRV stability.",
            "goal": "Stabilize your nighttime body temperature to enhance sleep quality and support autonomic recovery.",
            "how":
                [
                    {
                        "heading": "Maintain a Consistent Bedroom Temperature",
                        "text": "Set your room temperature between 65–70°F and avoid drastic fluctuations during the night. Use a thermostat or smart AC system if available to maintain stability."
                    },
                    {
                        "heading": "Choose Breathable Bedding and Sleepwear",
                        "text": "Opt for moisture-wicking, breathable fabrics such as cotton, bamboo, or merino wool. Avoid synthetic materials that trap heat and cause overheating."
                    },
                    {
                        "heading": "Use Temperature-Regulating Sleep Accessories",
                        "text": "Consider cooling mattress toppers, gel pillows, or moisture-wicking sheets to support consistent body temperature throughout the night."
                    },
                    {
                        "heading": "Limit Alcohol and Heavy Meals Before Bed",
                        "text": "Avoid alcohol and large meals within 2–3 hours of bedtime, as they can raise core body temperature and disrupt thermoregulation."
                    },
                    {
                        "heading": "Take a Warm Shower or Bath Before Bed",
                        "text": "A warm shower 60–90 minutes before bed helps promote a natural drop in core body temperature, enhancing readiness for deep sleep and temperature regulation."
                    },
                    {
                        "heading": "Avoid Intense Evening Exercise",
                        "text": "High-intensity workouts in the evening can elevate body temperature. If exercising late, opt for lighter activities such as yoga or stretching and allow time to cool down."
                    }
                ]
        }
    ];

    const goToPrevious = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? elitePerformanceProtocols.length - 1 : prevIndex - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex === elitePerformanceProtocols.length - 1 ? 0 : prevIndex + 1));
    };

    function findSuggestion(value) {
        const matchedSuggestion = suggestions.find(suggestion => suggestion.includes(value));

        if (!matchedSuggestion) {
            return { error: "No matching suggestion found." };
        }

        const lowerCaseSuggestion = matchedSuggestion.toLowerCase();
        let action = null;

        if (lowerCaseSuggestion.includes("try reducing")) {
            action = "try reducing";
        } else if (lowerCaseSuggestion.includes("try increasing")) {
            action = "try increasing";
        } else if (lowerCaseSuggestion.includes("maintain")) {
            action = "maintain";
        }

        // Extract the number at the end of the string
        const numberMatch = matchedSuggestion.match(/([\d.]+) ms\.$/);
        const number = numberMatch ? parseFloat(numberMatch[1]) : null;

        return { matchedSuggestion, action, number };
    }

    function extractNumber(inputString) {
        const match = inputString.match(/to (\d+(\.\d+)?)/);

        return match ? parseFloat(match[1]) : null;
    }

    function roundToRange(value) {
        // Calculate the lower boundary of the range
        const lowerBoundary = Math.round(value / 500) * 500;

        // Calculate the upper boundary of the range
        const upperBoundary = lowerBoundary + 500;

        return `${lowerBoundary} - ${upperBoundary}`;
    }

    const applyParameters = (pattern, parameters) => {
        return pattern.replace(/{(.*?)}/g, (_, key) => parameters[key.trim()] || '');
    }

    const renderAnalysisText = (insightText, column, textType) => {
        const featureIndex = features.indexOf(column);
        let text = insightText;
        if (column === 'Average Heart Rate') {
            const result = findSuggestion('Average Heart Rate');
            if (!result.error) {
                const value = values[featureIndex];
                let updatedText = "Elevated pre-sleep heart rate ({val} bpm) hinders deep sleep and HRV stability.";
                if (result.action === "maintain") {
                    updatedText = "Maintaining a lowered pre-sleep heart rate ({val} bpm) helps deep sleep and HRV stability.";
                }
                text = applyParameters(updatedText, {
                    val: value
                });

                return text;
            }
        } else if (column === "Total Calories" && textType && textType === "goal") {
            const result = findSuggestion('Total Calories');
            if (!result.error) {
                let value = values[featureIndex];
                let updatedText = "Maintain your total daily caloric intake of {val} calories to support optimal recovery and improve nocturnal HRV.";
                if (result.action === "try reducing") {
                    value = extractNumber(result.matchedSuggestion);
                    updatedText = "Decreasing your daily caloric intake to {val} calories to support optimal recovery and improve nocturnal HRV.";
                } else if (result.action === "try increasing") {
                    value = extractNumber(result.matchedSuggestion);
                    updatedText = "Increasing your daily caloric intake to {val} calories to support optimal recovery and improve nocturnal HRV.";
                }
                text = applyParameters(updatedText, {
                    val: value
                });

                return text;
            }
        } else if (column === "Total Calories" && textType && textType === "text") {
            const result = findSuggestion('Total Calories');
            if (!result.error) {
                let value = values[featureIndex];
                let updatedText = "Use a food tracker or app to monitor your daily caloric intake, ensuring it aligns with the target of {val} calories.";
                if (result.action === "try reducing") {
                    value = extractNumber(result.matchedSuggestion);
                } else if (result.action === "try increasing") {
                    value = extractNumber(result.matchedSuggestion);
                }
                text = applyParameters(updatedText, {
                    val: value
                });

                return text;
            }
        } else if (column === "Average Breathing Rate") {
            const result = findSuggestion('Average Breathing Rate');
            if (!result.error) {
                let value = values[featureIndex];
                let updatedText = "Breathing rate directly impacts autonomic function and recovery, influencing your nocturnal HRV. Maintaining an average breathing rate of {val} breaths per minute has contributed to a {num} ms improvement in your nocturnal HRV, but further refinement may yield additional benefits.";
                if (result.action === "try reducing") {
                    value = extractNumber(result.matchedSuggestion);
                    updatedText = "Breathing rate directly impacts autonomic function and recovery, influencing your nocturnal HRV. Reducing your Average Breathing Rate from {val} to {updatedVal} breaths per minute can contribute to a {num} ms improvement in your nocturnal HRV.";
                } else if (result.action === "try increasing") {
                    value = extractNumber(result.matchedSuggestion);
                    updatedText = "Breathing rate directly impacts autonomic function and recovery, influencing your nocturnal HRV. Increasing your Average Breathing Rate from {val} to {updatedVal} breaths per minute can contribute to a {num} ms improvement in your nocturnal HRV.";
                }
                text = applyParameters(updatedText, {
                    val: value,
                    num: result.number,
                    updatedVal: extractNumber(result.matchedSuggestion)
                });

                return text;
            }
        } else if (column === "Steps" && textType === "challenge") {
            const result = findSuggestion('Steps');
            if (!result.error) {
                const value = values[featureIndex];
                let updatedText = "Achieving optimal levels of daily movement is essential for regulating your circadian rhythm, improving recovery, and enhancing nocturnal HRV.";
                if (result.action === "maintain") {
                    updatedText = `${updatedText} Maintaining a step count of {val} reflects ideal activity levels to support these processes.`;
                }
                text = applyParameters(updatedText, {
                    val: value
                });

                return text;
            }
        } else if (column === "Steps" && textType === "goal") {
            const result = findSuggestion('Steps');
            if (!result.error) {
                const updatedText = "Sustain a daily step count of {range} steps to optimize physical activity, autonomic balance, and sleep quality.";
                let num = values[featureIndex];
                if (result.action !== "maintain") {
                    num = extractNumber(result.matchedSuggestion);
                }
                text = applyParameters(updatedText, {
                    range: roundToRange(num)
                });

                return text;
            }
        }
        if (featureIndex !== -1) {
            const value = values[featureIndex];
            text = applyParameters(insightText, {
                val: value
            });
        }
        return text;
    }

    const options = {
        animationEnabled: true,
        title: {
            text: "Top 6 Factors Affecting your Nocturnal HRV",
            fontColor: "#426bb3",
            fontFamily: "Arial, sans-serif",
            fontWeight: "700"
        },
        data: [{
            type: "pyramid",
            highlightEnabled: false,
            dataPoints: sortedFeatures.map((feature, index) => {
                const yVal = Number(sortedContributions[index].toFixed(0));
                return { label: feature, y: yVal, color: pyramidColors[index], toolTipContent: `<span>${sortedExplanations[index]}</span> <hr/> <b>{label}: {y}%</b>` };
            })
        }]
    }

    // const handleSelect = (button) => {
    //     setLoading(true);
    //     setSelectedPeriod(button);
    // };

    const onChartIconClick = (chartFeature) => {
        setSelectedBarChartFeature(chartFeature);
        setShowBarChart(true);
    };

    const renderBarChart = () => {
        const slide = elitePerformanceProtocols.find(item => item.column === selectedBarChartFeature);
        const arrayData = predictionsQuarterlyData.feature_buckets[selectedBarChartFeature];
        const { ideal_bucket, current_bucket } = arrayData;

        // Create datapoints
        const idealX = (parseInt(ideal_bucket.min) + parseInt(ideal_bucket.max)) / 2;
        const currentX = (parseInt(current_bucket.min) + parseInt(current_bucket.max)) / 2;

        // Sort by x-axis so chart renders left-to-right
        const points = [
            {
            label: 'Ideal',
            x: idealX,
            y: ideal_bucket.average_hrv,
            backgroundColor: 'green',
            radius: 8,
            },
            {
            label: 'Current',
            x: currentX,
            y: current_bucket.average_hrv,
            backgroundColor: 'brown',
            radius: 8,
            }
        ].sort((a, b) => a.x - b.x);

        const maxY = Math.ceil(Math.max(ideal_bucket.average_hrv, current_bucket.average_hrv) + 10);

        const scatterData = {
            datasets: [
            {
                label: 'HRV Buckets',
                data: points,
                showLine: true,
                borderColor: 'gray',
                borderDash: [5, 5],
                pointBackgroundColor: points.map(p => p.backgroundColor),
                pointRadius: points.map(p => p.radius),
                pointHoverRadius: 10,
            }
            ]
        };

        const options = {
            responsive: true,
            scales: {
            x: {
                title: {
                display: true,
                text: selectedBarChartFeature,
                },
            },
            y: {
                min: 0,
                max: maxY,
                title: {
                display: true,
                text: 'Average HRV',
                },
            },
            },
            plugins: {
            legend: {
                display: false,
            },
            },
        };

        // const labels = arrayData.map((item) => `${item.min} - ${item.max}`);
    
        // const barChartData = {
        //   labels,
        //   datasets: [
        //   {
        //       label: "Average HRV",
        //       data: arrayData.map((item) => item.average_hrv.toFixed(1)),
        //       backgroundColor: "rgba(54, 162, 235, 0.6)", // Blue color
        //       borderColor: "rgba(54, 162, 235, 1)",
        //       borderWidth: 1,
        //   },
        //   ],
        // };
      
        // const barChartOptions = {
        //   responsive: true,
        //   plugins: {
        //   legend: { display: false }, // Hide legend since there's only one dataset
        //   },
        //   scales: {
        //   y: {
        //       title: { display: true, text: "Average HRV" },
        //       beginAtZero: true,
        //   },
        //   x: {
        //       title: { display: true, text: selectedBarChartFeature },
        //   },
        //   },
        // };

        return (
            <div>
                <h4 className="recommendation-header">Your Path to Greater Energy, Resilience, and Recovery</h4>
                {/* {slide &&
                    <div className="content-container">
                        <p><b>{slide.heading}</b></p>
                        <p><b>Challenge: </b>{renderAnalysisText(slide.challenge, slide.column, "challenge")}</p>
                        <p><b>Goal: </b>{(slide.column === "Total Calories" || slide.column === "Steps") ? renderAnalysisText(slide.goal, slide.column, "goal") : slide.goal}</p>
                        <p className="how-text-not-visible"><b>How: </b></p>
                        <ul>
                            {slide.how.map((item, ind) => (
                                <li key={`pyramid-slide-content-${ind}`}>
                                    <p><b>{item.heading}: </b>
                                        {(slide.column === "Total Calories" && ind === 0) &&
                                            <>{renderAnalysisText(item.text, slide.column, "text")}</>
                                        }
                                        {((slide.column === "Total Calories" && ind !== 0) || slide.column !== "Total Calories") &&
                                            <>{item.text}</>
                                        }
                                    </p>
                                </li>
                            ))}
                        </ul>
                    </div>
                } */}
                <button className="close-modal-button" onClick={() => setShowBarChart(false)}>×</button>
                {/* <Bar className="pyramid-bar-chart" data={barChartData} options={barChartOptions} /> */}
                <Scatter data={scatterData} options={options} />
            </div>
        );
    }

    return (
        <>
            <div className="pyramid-chart">
            {/* <h4 className="page-title section-header">Top 6 Factors Affecting your Nocturnal HRV</h4> */}
                <button className="ai-recco-button" onClick={openModal}>Concierge Insights</button>
                {/* <div className="toggle-button-container">
                    <button
                        onClick={() => handleSelect('monthly-score')}
                        className="toggle-button"
                        style={{
                            background: selectedPeriod === 'monthly-score' ? 'rgb(124 202 204)' : '#f0f0f0',
                            color: selectedPeriod === 'monthly-score' ? '#fff' : '#000',
                        }}
                    >
                        Monthly
                    </button>

                    <button
                        onClick={() => handleSelect('quarterly-score')}
                        className="toggle-button"
                        style={{
                            background: selectedPeriod === 'quarterly-score' ? 'rgb(124 202 204)' : '#f0f0f0',
                            color: selectedPeriod === 'quarterly-score' ? '#fff' : '#000',
                        }}
                    >
                        Quarterly
                    </button>
                </div> */}
                {/* {!loading && */}
                    <div className="pyramid-chart-container">
                        <div className="hide-watermark" ></div>
                        <CanvasJSChart options={options} />
                    </div>
                {/* } */}
                {isModalOpen && (
                    <div className="modal-overlay">
                        <div className="modal-content concierge-insights-modal" ref={recommendationModalRef}>
                            <button className="close-modal-button" onClick={closeModal}>×</button>
                            {/* <div className="recco-list">
                                <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
                                {(!recommendation || !Object.keys(recommendation).length) &&
                                    <div className="no-data-text">No AI recommendations to present.</div>
                                }
                            </div> */}
                            <div className="dashboard-carousel pyramid-insights">
                                <h4 className="recommendation-header">CONCIERGE INSIGHTS</h4>
                                <div className="tabs">
                                    <button
                                        className={activeTab === 0 ? 'active' : ''}
                                        onClick={() => setActiveTab(0)}
                                    >
                                        Analysis of Today's Insights
                                    </button>
                                    {/* <button
                                        className={activeTab === 1 ? 'active' : ''}
                                        onClick={() => setActiveTab(1)}
                                    >
                                        Elite Performance Protocol
                                    </button> */}
                                </div>
                                <div className="tab-content">
                                    {activeTab === 0 &&
                                        <>
                                            {!showBarChart && 
                                                <div>
                                                    {/* <h4>Insights from our AI algorithm:</h4> */}
                                                    <ul className="analysis-ul">
                                                        {recommendations[0].main_value.map((insight, index) => {
                                                            // Filter the sub-items based on features
                                                            // const filteredValues = insight.value.filter((val) =>
                                                            //     features.includes(val.sub_heading)
                                                            // );
                                                
                                                            // If no sub-items match, don't render this main item
                                                            // if (filteredValues.length === 0) return null;
                                                
                                                            return (
                                                            <li key={`pyramid-chart-insight-${index}`}>
                                                                <h4 className="insight-heading">{insight.heading}</h4>
                                                                <ul>
                                                                    {insight.value.map((val, ind) => (
                                                                        <li key={`pyramid-chart-insight-val-${index}-${ind}`} >
                                                                            <p><b>{val.sub_heading}: </b>{val.sub_value.sub_text}</p>
                                                                            <ul className="analysis-ul-list-item">
                                                                                {val.sub_value.sub_sub_values.map((subVal, subInd) => (
                                                                                    <li className="pyramid-chart-insight-listitem" key={`pyramid-chart-insight-subval-${index}-${ind}-${subInd}`}>
                                                                                        <p><b>{subVal.heading}: </b>{subVal.value}</p>
                                                                                        {subVal.heading === "Optimization Target" &&
                                                                                            <i className="column-chart-icon fa-solid fa-chart-column" onClick={() => onChartIconClick(val.sub_heading)}></i>
                                                                                        }
                                                                                        {subVal.value_list.length > 0 && 
                                                                                            <ul>
                                                                                                {subVal.value_list.map((listItem, listInd) => (
                                                                                                    <li key={`pyramid-chart-insight-subval-list-${index}-${ind}-${subInd}-${listInd}`}>
                                                                                                        <p><b>{listItem.sub_heading}: </b>{listItem.sub_value}</p>
                                                                                                    </li>
                                                                                                ))}
                                                                                            </ul>
                                                                                        }
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            </li>
                                                            )
                                                        })}
                                                    </ul>
                                                </div>
                                            }
                                            {showBarChart &&
                                                <>
                                                    {renderBarChart()}
                                                </>
                                            }
                                        </>
                                    }
                                    {/* {activeTab === 1 &&
                                        <>
                                            <h4 className="recommendation-header">Your Path to Greater Energy, Resilience, and Recovery</h4>
                                            <ul>
                                                {elitePerformanceProtocols.map((slide, index) => (
                                                    <li key={`pyramid-slide-${index}`} className="content-container">
                                                        <p><b>{slide.heading}</b></p>
                                                        <p><b>Challenge: </b>{renderAnalysisText(slide.challenge, slide.column, "challenge")}</p>
                                                        <p><b>Goal: </b>{(slide.column === "Total Calories" || slide.column === "Steps") ? renderAnalysisText(slide.goal, slide.column, "goal") : slide.goal}</p>
                                                        <p className="how-text-not-visible"><b>How: </b></p>
                                                        <ul>
                                                            {slide.how.map((item, ind) => (
                                                                <li key={`pyramid-slide-content-${index}-${ind}`}>
                                                                    <p><b>{item.heading}: </b>
                                                                        {(slide.column === "Total Calories" && ind === 0) &&
                                                                            <>{renderAnalysisText(item.text, slide.column, "text")}</>
                                                                        }
                                                                        {((slide.column === "Total Calories" && ind !== 0) || slide.column !== "Total Calories") &&
                                                                            <>{item.text}</>
                                                                        }
                                                                    </p>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <hr/>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    } */}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <hr className="divider" />
            {/* {!loading && */}
                <div className="metrics-grid metrics-grid-3 even-grid">
                    {sortedFeatures.map((feature, index) => (
                        <div key={`hrvAffectingFactor-${index}`} className="metric-cell">
                            <div className="metric-header">{feature}</div>
                            <div className="metric-value">{sortedValues[index]}</div>
                        </div>
                    ))}
                </div>
            {/* } */}
        </>
    );
};

export default PyramidChart;
